import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Globe } from "lucide-react";
import { ASSETS, NAV_IDS } from "../lib/data";
import { useT } from "../lib/i18n";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const Header = () => {
  const { t, lang, toggle } = useT();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (id) => {
    setOpen(false);
    setTimeout(() => scrollTo(id), 50);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 border-b border-gold/40 ${
        scrolled
          ? "bg-black/80 backdrop-blur-xl"
          : "bg-black/55 backdrop-blur-md"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-24 md:h-28 flex items-center justify-between">
        <button
          data-testid="logo-home-btn"
          onClick={() => scrollTo("top")}
          className="flex items-center gap-3 group"
          aria-label="Monsieur Toast"
        >
          <img
            src={ASSETS.logoGold}
            alt="Monsieur Toast"
            className="h-24 md:h-28 w-auto select-none -my-6 transition-all duration-500"
            draggable="false"
          />
        </button>

        <nav className="hidden md:flex items-center gap-10" data-testid="primary-nav">
          {NAV_IDS.map((id) => (
            <button
              key={id}
              data-testid={`nav-link-${id}`}
              onClick={() => scrollTo(id)}
              className="text-[12px] tracking-[0.25em] uppercase text-white/85 hover:text-gold transition-colors"
            >
              {t(`nav.${id}`)}
            </button>
          ))}
          <a
            data-testid="nav-cta-order"
            href="#menu"
            onClick={(e) => {
              e.preventDefault();
              scrollTo("menu");
            }}
            className="text-[12px] tracking-[0.25em] uppercase text-gold border border-gold/40 px-5 py-3 hover:bg-gold hover:text-black transition-colors"
          >
            {t("nav.cta")}
          </a>
          <button
            data-testid="lang-toggle"
            onClick={toggle}
            aria-label="Change language"
            className="flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-white/80 hover:text-gold transition-colors border border-gold/25 hover:border-gold px-3 py-2"
          >
            <Globe size={14} className="text-gold" />
            <span className="font-semibold">{lang.toUpperCase()}</span>
            <span className="text-white/40">/</span>
            <span className="text-white/40">{t("lang.toggle")}</span>
          </button>
        </nav>

        <div className="flex items-center gap-2 md:hidden">
          <button
            data-testid="lang-toggle-mobile"
            onClick={toggle}
            aria-label="Change language"
            className="flex items-center gap-1.5 text-[11px] tracking-[0.2em] uppercase text-gold border border-gold/30 px-3 py-2"
          >
            <Globe size={13} />
            <span className="font-semibold">{lang.toUpperCase()}</span>
          </button>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="text-white p-2"
            aria-label="Open menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="md:hidden bg-black border-t border-gold/15 overflow-hidden"
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              {NAV_IDS.map((id) => (
                <button
                  key={id}
                  data-testid={`mobile-nav-${id}`}
                  onClick={() => handleNav(id)}
                  className="text-left text-2xl font-display text-white hover:text-gold transition-colors"
                >
                  {t(`nav.${id}`)}
                </button>
              ))}
              <button
                data-testid="mobile-nav-cta"
                onClick={() => handleNav("menu")}
                className="mt-2 self-start text-[12px] tracking-[0.25em] uppercase text-gold border border-gold/40 px-5 py-3"
              >
                {t("nav.cta")}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
